import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../../utils/forecastingApi';
import '../../Styles/SFChatWidget.css';

const SF_WIDGET_STORAGE_KEY = 'sf_widget_state_v1';

const WELCOME_MESSAGE = {
  id: 'welcome',
  sender: 'ai',
  text: "Hi! I'm your Sales Forecasting Assistant. Ask me about forecasts, quotas, performance, or adjustments.",
  time: '',
};

const QUICK_PROMPTS = [
  "What is Elangovan's forecast for Q3?",
  "Show pending quota adjustments",
  "Which reps are underperforming this quarter?",
  "What's the average attainment across all reps?",
];

function nowLabel() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function buildSessionId() {
  return `sf-widget-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function makeSessionTitle(question) {
  const clean = (question || '').trim();
  if (!clean) return 'New chat';
  return clean.length > 30 ? `${clean.slice(0, 30)}...` : clean;
}

function buildSession(title = 'New chat') {
  return {
    id: buildSessionId(),
    title,
    updatedAt: Date.now(),
    sessionId: null,
    messages: [{ ...WELCOME_MESSAGE, id: `welcome-${Date.now()}` }],
  };
}

function getInitialState() {
  try {
    const raw = localStorage.getItem(SF_WIDGET_STORAGE_KEY);
    if (!raw) {
      const s = buildSession('Chat 1');
      return { open: false, expanded: false, fullScreen: false, activeSessionId: s.id, sessions: [s] };
    }
    const parsed = JSON.parse(raw);
    const savedSessions = Array.isArray(parsed.sessions) && parsed.sessions.length > 0
      ? parsed.sessions : [buildSession('Chat 1')];
    const activeSessionId = savedSessions.some(s => s.id === parsed.activeSessionId)
      ? parsed.activeSessionId : savedSessions[0].id;
    return {
      open: false,
      expanded: typeof parsed.expanded === 'boolean' ? parsed.expanded : false,
      fullScreen: false,
      activeSessionId,
      sessions: savedSessions,
    };
  } catch (_) {
    const s = buildSession('Chat 1');
    return { open: false, expanded: false, fullScreen: false, activeSessionId: s.id, sessions: [s] };
  }
}

const SFChatWidget = () => {
  const initial = useMemo(() => getInitialState(), []);
  const [open,            setOpen]            = useState(initial.open);
  const [expanded,        setExpanded]        = useState(initial.expanded);
  const [fullScreen,      setFullScreen]      = useState(initial.fullScreen);
  const [input,           setInput]           = useState('');
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState('');
  const [sessions,        setSessions]        = useState(initial.sessions);
  const [activeSessionId, setActiveSessionId] = useState(initial.activeSessionId);
  const [editingSessionId,setEditingSessionId]= useState(null);
  const [renameValue,     setRenameValue]     = useState('');

  const messagesEndRef = useRef(null);

  const activeSession = useMemo(
    () => sessions.find(s => s.id === activeSessionId) || sessions[0],
    [sessions, activeSessionId]
  );

  const messages = activeSession?.messages || [WELCOME_MESSAGE];

  // Persist state (but always start closed)
  useEffect(() => {
    localStorage.setItem(SF_WIDGET_STORAGE_KEY, JSON.stringify({
      open, expanded, fullScreen, activeSessionId, sessions,
    }));
  }, [open, expanded, fullScreen, activeSessionId, sessions]);

  // Esc to exit fullscreen
  useEffect(() => {
    if (!fullScreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setFullScreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullScreen]);

  useEffect(() => {
    if (fullScreen) document.body.classList.add('sf-widget-fullscreen-active');
    else            document.body.classList.remove('sf-widget-fullscreen-active');
    return () => document.body.classList.remove('sf-widget-fullscreen-active');
  }, [fullScreen]);

  // Scroll to bottom
  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [open, messages, loading]);

  const canSend = useMemo(() => input.trim().length > 0 && !loading, [input, loading]);

  const updateActiveSession = (patch) => {
    setSessions(prev => prev.map(s =>
      s.id === activeSessionId ? { ...s, ...patch, updatedAt: Date.now() } : s
    ));
  };

  const sendMessage = async (question) => {
    const clean = (question || '').trim();
    if (!clean || loading) return;

    const userMsg = { id: `u-${Date.now()}`, sender: 'user', text: clean, time: nowLabel() };
    const nextTitle = messages.length <= 1 ? makeSessionTitle(clean) : activeSession?.title;
    const nextMessages = [...messages, userMsg];

    updateActiveSession({ title: nextTitle, messages: nextMessages });
    setInput(''); setError(''); setLoading(true);

    try {
      const payload = { message: clean };
      if (activeSession?.sessionId) payload.session_id = activeSession.sessionId;

      const res = await api.sendChat(payload);

      const aiMsg = {
        id: `a-${Date.now()}`,
        sender: 'ai',
        text: res.reply || 'No response returned.',
        time: nowLabel(),
        tool_used: res.tool_used || null,
      };

      updateActiveSession({
        messages: [...nextMessages, aiMsg],
        sessionId: res.session_id || activeSession?.sessionId || null,
      });
    } catch (err) {
      setError(err.message || 'Failed to get a response.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); if (canSend) sendMessage(input); };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (canSend) sendMessage(input); }
  };

  const handleNewChat = () => {
    const s = buildSession(`Chat ${sessions.length + 1}`);
    setSessions(prev => [s, ...prev]);
    setActiveSessionId(s.id);
    setInput(''); setError(''); setLoading(false);
  };

  const handleClearChat = () => {
    updateActiveSession({ title: 'New chat', messages: [{ ...WELCOME_MESSAGE, id: `welcome-${Date.now()}` }], sessionId: null });
    setError('');
  };

  const handleStartRename = (session) => { setEditingSessionId(session.id); setRenameValue(session.title || 'New chat'); };
  const handleCancelRename = () => { setEditingSessionId(null); setRenameValue(''); };
  const handleSaveRename = (sessionId) => {
    const t = (renameValue || '').trim();
    if (!t) return;
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, title: t, updatedAt: Date.now() } : s));
    handleCancelRename();
  };

  const handleDeleteSession = (sessionId) => {
    setSessions(prev => {
      const remaining = prev.filter(s => s.id !== sessionId);
      if (remaining.length === 0) { const fb = buildSession('Chat 1'); setActiveSessionId(fb.id); return [fb]; }
      if (activeSessionId === sessionId) setActiveSessionId(remaining[0].id);
      return remaining;
    });
    if (editingSessionId === sessionId) handleCancelRename();
  };

  const sortedSessions = useMemo(
    () => [...sessions].sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0)),
    [sessions]
  );

  return (
    <div className={`sf-widget-container ${open ? 'is-open' : ''} ${fullScreen ? 'fullscreen' : ''}`}>

      {/* ── Floating toggle button ── */}
      {!open && (
        <button type="button" className="sf-widget-toggle" onClick={() => setOpen(true)}
          aria-label="Open Sales Forecasting AI Assistant">
          <span className="sf-widget-toggle-dot" />
          Ask AI
        </button>
      )}

      {/* ── Chat panel ── */}
      {open && (
        <section className={`sf-widget-panel ${expanded ? 'expanded' : ''}`} aria-label="SF Chat Widget">

          {/* Header */}
          <header className="sf-widget-header">
            <div className="sf-widget-title">
              <h3>Sales Forecasting AI</h3>
              <p>{loading ? 'Thinking…' : 'Live AI answers'}</p>
            </div>
            <div className="sf-widget-actions">
              <button type="button" onClick={handleNewChat}>New</button>
              <button type="button" onClick={handleClearChat}>Clear</button>
              <button type="button" onClick={() => setExpanded(prev => !prev)}>{expanded ? 'Min' : 'Max'}</button>
              {fullScreen
                ? <button type="button" onClick={() => setFullScreen(false)}>Exit</button>
                : <button type="button" onClick={() => { setExpanded(true); setFullScreen(true); }}>Full</button>
              }
              <button type="button" onClick={() => { setOpen(false); setFullScreen(false); }}>✕</button>
            </div>
          </header>

          {/* Body */}
          <div className="sf-widget-body">

            {/* Sessions sidebar (only when expanded) */}
            {expanded && (
              <aside className="sf-widget-sessions" aria-label="Chat sessions">
                <h4>Sessions</h4>
                <div className="sf-widget-session-list">
                  {sortedSessions.map(session => (
                    <div key={session.id}
                      className={`sf-widget-session-item ${session.id === activeSessionId ? 'active' : ''}`}>
                      {editingSessionId === session.id ? (
                        <div className="sf-widget-session-rename" onClick={e => e.stopPropagation()}>
                          <input value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); handleSaveRename(session.id); }
                              if (e.key === 'Escape') { e.preventDefault(); handleCancelRename(); }
                            }}
                            autoFocus />
                          <div className="sf-widget-session-rename-actions">
                            <button type="button" onClick={() => handleSaveRename(session.id)}>Save</button>
                            <button type="button" onClick={handleCancelRename}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button type="button" className="sf-widget-session-main"
                            onClick={() => { setActiveSessionId(session.id); setError(''); }}>
                            <strong>{session.title || 'New chat'}</strong>
                            <span>{new Date(session.updatedAt || Date.now()).toLocaleString([], { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          </button>
                          <div className="sf-widget-session-item-actions">
                            <button type="button" onClick={() => handleStartRename(session)}>Edit</button>
                            <button type="button" onClick={() => handleDeleteSession(session.id)}>Del</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </aside>
            )}

            {/* Main chat area */}
            <div className="sf-widget-main">
              <div className="sf-widget-messages">

                {/* Quick prompts (only when only welcome message present) */}
                {messages.length <= 1 && (
                  <div className="sf-widget-quick-prompts">
                    {QUICK_PROMPTS.map(prompt => (
                      <button key={prompt} type="button" onClick={() => sendMessage(prompt)}>
                        {prompt}
                      </button>
                    ))}
                  </div>
                )}

                {/* Message bubbles */}
                {messages.map(msg => (
                  <article key={msg.id} className={`sf-widget-message ${msg.sender === 'user' ? 'user' : 'ai'}`}>
                    <div className="sf-widget-bubble">
                      <p>{msg.text}</p>
                      {msg.tool_used && (
                        <span className="sf-widget-tool-badge">tool: {msg.tool_used}</span>
                      )}
                      <small className="sf-widget-time">{msg.time || ''}</small>
                    </div>
                  </article>
                ))}

                {/* Typing indicator */}
                {loading && (
                  <div className="sf-widget-typing">
                    <span /><span /><span />
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {error && <p className="sf-widget-error">{error}</p>}

              <form className="sf-widget-input" onSubmit={handleSubmit}>
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  placeholder="Ask about forecasts, quotas, performance…"
                />
                <button type="submit" disabled={!canSend}>
                  {loading ? '…' : 'Send'}
                </button>
              </form>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default SFChatWidget;
